import { validateUsername } from "../account";

/**
 * General validation class. This can be used for any form on the site.
 * Extend for even more validation fun.
 * Everything is attached on blur
 */
export class FormValidation {
    constructor(private form: HTMLFormElement) {
        // We have to bind this or it don't understand, must be done first
        this.checkValidation = this.checkValidation.bind(this);
        this.attachEventListeners = this.attachEventListeners.bind(this);

        this.attachEventListeners();
    }

    /**
     * Pass in an array that will manually be validated
     */
    public manualValidation(errors: [{ name: string; errorMessage: string }]): boolean | void {
        for (const { name, errorMessage } of errors) {
            // Grab the input, not the error div
            const formInput = document.getElementById(name) as HTMLInputElement | null;

            if (formInput && formInput.willValidate) {
                this.showError(formInput, errorMessage);
            } else {
                this.nonInputError(name, errorMessage);
            }
        }

        // If an error is actually added let's scroll it into view
        if (document.querySelector(".error-message--active")) {
            document.querySelector(".error-message--active")!.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }

    /**
     * Pass in an element and manually remove the error.
     * Basically just a wrapper for this.removeError
     */
    public manualRemoveError(formInput: HTMLInputElement): void {
        this.removeError(formInput);
    }

    /**
     * Loop through all form fields and validate them.
     */
    public validateAllFields(): boolean {
        const fields = this.form.querySelectorAll<HTMLInputElement>("input, textarea");

        for (const field of fields) {
            this.checkValidation(field);
        }

        if (this.form.checkValidity()) {
            return true;
        }

        // If an error is actually added let's scroll it into view
        if (document.querySelector(".error-message--active")) {
            document.querySelector(".error-message--active")!.scrollIntoView({ behavior: "smooth", block: "center" });
        }

        return false;
    }

    /**
     * Check the validation for the form.
     * Send off to proper method as necessary.
     */
    protected checkValidation(formInput: HTMLInputElement | null): boolean | Promise<boolean | undefined> | undefined {
        // Make sure the element exists and can be validated. willValidate is needed for IE.
        if (!formInput || !formInput.willValidate) {
            return false;
        }

        // All is well, no need to show error
        if (formInput.checkValidity()) {
            this.removeError(formInput);

            return true;
        }

        // Get an object of the forms validity state
        const validness = formInput.validity;

        if (validness.valueMissing) {
            this.showError(formInput);
        } else if (validness.typeMismatch) {
            this.showError(formInput, "Please enter a valid email.");
        } else {
            this.showError(formInput, formInput.validationMessage);
        }
    }

    /**
     * Show the error on the form
     */
    protected showError(formInput: HTMLInputElement, errorMessage = ""): void {
        const id = formInput.id;
        const errorElement = document.getElementById(`${id}-error`);

        if (errorElement) {
            const { errorTitle } = errorElement.dataset;

            if (errorMessage === "" && errorTitle) {
                errorMessage = `Please enter your ${errorTitle}.`;
            }
            errorElement.innerText = errorMessage;
            errorElement.classList.add("error-message--active");
        }
    }

    /**
     * Let's go ahead and remove the error
     */
    protected removeError(formInput: HTMLInputElement): void {
        const id = formInput.id;
        const errorMessage = document.getElementById(`${id}-error`);

        // Let's remove the error
        if (errorMessage) {
            errorMessage.classList.remove("error-message--active");
        }
    }

    protected determineLength(formInput: HTMLInputElement) {
        if (formInput.id === "password") {
            return this.showError(formInput, "The password must be at least 8 characters.");
        }

        if (formInput.id === "zip") {
            return this.showError(formInput, "Please enter a valid zip code.");
        }

        if (formInput.id === "phone") {
            return this.showError(formInput, "Please enter a valid phone number.");
        }
    }

    /**
     * Make sure the username hasn't been used before
     */
    protected async checkUsername(usernameField: HTMLInputElement): Promise<void> {
        const { valid } = await validateUsername(usernameField.value);

        // The username doesn't exist, so return early
        if (valid) {
            usernameField.setCustomValidity("");
            return;
        }

        usernameField.setCustomValidity("A user with that email already exists.");

        return Promise.resolve();
    }

    /**
     * An error not associated with one input.
     * Typically shows in a div at the top of a section
     */
    private nonInputError(errorDiv: string, errorMessage = "There was a problem submitting the form. Please refresh the page and try again.") {
        const generalErrorElement = document.getElementById(`${errorDiv}-error`);

        if (generalErrorElement) {
            generalErrorElement.innerText = errorMessage;
            generalErrorElement.classList.add("error-message--active");
        }
    }

    /**
     * Attach needed event listeners
     */
    private attachEventListeners() {
        if (this.form) {
            this.form.addEventListener("blur", (event) => this.checkValidation(event.target as HTMLInputElement | null), true);
        }
    }
}
